/* eslint-disable no-await-in-loop */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UncontrolledTooltip, ModalBody, Spinner } from 'reactstrap';
import moment from 'moment';
import uuid from 'react-uuid';

import { setCurrentNotification } from 'redux/actions/notificationActions';
import {
  goBackClientNotify,
  saveAttachments,
  saveAnswers,
  setLastNotifyId,
  saveContactAnswers,
} from 'redux/actions/notificationReviewActions';
import useApi from 'hooks/useApi';
import { showAlert } from 'redux/actions/alertActions';

import { Button } from 'shared/components/Buttons';
import { colors } from 'shared/defaultTheme';
import MainSpinner from 'shared/components/MainSpinner';
import AlertModal from 'shared/components/AlertModal';
import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import PreviewNotification from 'shared/components/PreviewNotification';
import AttachForm from 'shared/components/AttachForm';
import {
  getIconAccessDocument,
  getIconCode,
  getIconDoc,
  getIconExcel,
  getIconImage,
  getIconPdf,
  getIconPpt,
  getIconSound,
  getIconVideo,
  getIconZip,
  ALERT_TYPE_SUCCESS,
} from 'shared/utils';
import AttachmentBox from 'shared/components/AttachmentBox';
import { UploadedImage } from 'shared/components/SharedStyles/SharedStyles';

import { ReactComponent as ConversationIcon } from 'assets/icon-conversation.svg';
import { ReactComponent as WordIcon } from 'assets/icon-word.svg';
import { ReactComponent as DocumentIcon } from 'assets/icon-document.svg';
import { ReactComponent as PdfIcon } from 'assets/icon-pdf.svg';
import { ReactComponent as ImgIcon } from 'assets/icon-img.svg';
import { ReactComponent as ExcelIcon } from 'assets/icon-excel.svg';
import { ReactComponent as HtmlIcon } from 'assets/icon-html.svg';
import { ReactComponent as SoundIcon } from 'assets/icon-sound.svg';
import { ReactComponent as VideoIcon } from 'assets/icon-video.svg';
import { ReactComponent as AccessDocumentIcon } from 'assets/icon-access-document.svg';
import { ReactComponent as PowerPointIcon } from 'assets/icon-ppt.svg';
import { ReactComponent as ZipIcon } from 'assets/icon-zip.svg';
import { ReactComponent as EyeIcon } from 'assets/icon-visible.svg';
import { ReactComponent as UploaderIcon } from 'assets/icon-up-file.svg';
import { PREVIEW_NOTIFICATION } from 'shared/constants/variants';
import useNotification from 'hooks/useNotification';
import NotificationCommentCard from '../CommentCard';
import { ChatWrapper, AttachFormModal, ButtonPreview, ButtonUpload } from './styles';

const Chat = ({ newName }) => {
  const dispatch = useDispatch();
  const { customAreaApi } = useApi();
  const history = useHistory();
  const { t } = useTranslation();
  const {
    mail,
    cusNavId,
    clientId,
    authToken,
    isCustomer,
    name: authenticatedName,
  } = useSelector((state) => state.auth);
  const theme = useSelector((state) => state.theme);
  const { currentClient } = useSelector((state) => state.clientState);
  const {
    answers,
    currentNotificationReview,
    lastNotifyId,
    currentNotificationReviewId,
    attachments,
  } = useSelector((state) => state.notificationReview);
  const { showTypeNotifications } = useSelector((state) => state.notification);
  const refRichTextEditor = useRef(null);
  const [showError, setShowError] = useState(false);
  const { mainColor } = theme;
  const [answerText, setAnswerText] = useState('');
  const { state } = useLocation();
  const [messageError, setMessageError] = useState('');
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentData, setCurrentData] = useState('');

  const { refreshCurrentNotificationReview, markAsRead } = useNotification();

  const [{ loading: createAnswerLoading }, createAnswer] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: createMassiveAnswerLoading }, createMassiveAnswer] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const [{ loading: createGeneralContactAnswerLoading }, createGeneralContactAnswer] =
    customAreaApi(
      {
        method: 'POST',
      },
      { manual: true }
    );

  const [{ loading: loadingDownload }, getDownload] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const [{ loading: loadingMassiveDownload }, getMassiveDownload] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const [, getDocumentData] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const [, getDocumentMassiveData] = customAreaApi(
    {
      method: 'GET',
    },
    { manual: true }
  );

  const onToggleError = () => setShowError((currentShow) => !currentShow);

  useEffect(() => {
    if (!currentNotificationReview) return;
    dispatch(setLastNotifyId(currentNotificationReview.id));
    const loadImg = async () => {
      let newAnswers = [];
      if (answers?.length > 0) {
        for (let index = 0; index < answers.length; index += 1) {
          const answer = answers[index];
          if (answer.docs) {
            for (let i = 0; i < answer.docs.length; i += 1) {
              const document = answer.docs[i];
              if (
                document.type.toLowerCase() === '.png' ||
                document.type.toLowerCase() === '.jpg'
              ) {
                const {
                  data: { data },
                } = !showTypeNotifications
                  ? await getDocumentData({
                      url: `${window.API_V1}/cli/notifications/${cusNavId}/${
                        currentClient?.clientId ? currentClient?.clientId : clientId
                      }/${encodeURIComponent(mail)}/${currentNotificationReview.id}/get/${
                        document.id
                      }`,
                    })
                  : await getDocumentMassiveData({
                      url: `${window.API_V1}/gen/not/${cusNavId}/${encodeURIComponent(mail)}/${
                        currentNotificationReview.id
                      }/get/${document.id}`,
                    });
                document.data = data?.data;
                document.isLoadingImg = false;
              }
            }
          }
          newAnswers = [...newAnswers, { ...answer }];
        }
        dispatch(setLastNotifyId(currentNotificationReview.id));
        dispatch(saveAnswers(newAnswers));
      }
    };
    loadImg();
    (async () => {
      await markAsRead(currentNotificationReview.id);
    })();
  }, [currentNotificationReview]);

  useEffect(() => {
    if (currentNotificationReview.id !== lastNotifyId) {
      dispatch(saveAnswers(currentNotificationReview.answers));
    }
  }, [currentNotificationReview, lastNotifyId]);

  const answerSave = async () => {
    try {
      const {
        data: { dataErrors },
      } = !showTypeNotifications
        ? await createAnswer({
            data: [
              {
                id: uuid(),
                text: answerText,
                createdBy: mail,
                docs: attachments,
              },
            ],
            url: `${window.API_V1}/cli/notifications/${cusNavId}/${
              currentClient?.clientId || clientId
            }/${currentNotificationReviewId}/answer`,
          })
        : await createMassiveAnswer({
            data: [
              {
                id: uuid(),
                text: answerText,
                createdBy: mail,
                docs: attachments,
                createdByName: authenticatedName,
              },
            ],
            url: `${window.API_V1}/gen/not/${cusNavId}/${currentNotificationReviewId}/answer`,
          });
      if (dataErrors) {
        throw new Error(
          dataErrors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
      setAnswerText('');
      dispatch(saveAttachments([]));
      await refreshCurrentNotificationReview();
    } catch (error) {
      setMessageError(t('custom_field_operation_error_message'));
      setShowError(true);
    }
  };

  const answerContactSave = async () => {
    try {
      const {
        data: { dataErrors, data },
      } = !showTypeNotifications
        ? await createAnswer({
            data: [
              {
                id: uuid(),
                text: answerText,
                createdBy: mail,
                createdByName: authenticatedName,
                docs: attachments,
              },
            ],
            url: `${window.API_V1}/cli/notifications/${cusNavId}/${
              currentClient?.clientId || clientId
            }/${currentNotificationReviewId}/answer`,
          })
        : await createGeneralContactAnswer({
            data: [
              {
                id: uuid(),
                text: answerText,
                createdBy: mail,
                createdByName: authenticatedName,
                docs: attachments,
              },
            ],
            url: `${window.API_V1}/gen/not/${cusNavId}/${currentNotificationReviewId}/answerContact`,
          });
      if (dataErrors) {
        throw new Error(
          dataErrors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
      setAnswerText('');
      dispatch(saveAttachments([]));
      if (!showTypeNotifications) {
        await refreshCurrentNotificationReview();
      } else {
        dispatch(saveContactAnswers(data.id));
        dispatch(
          showAlert({
            alertType: ALERT_TYPE_SUCCESS,
            alertMessage: t('notification_save_general_contact_answers'),
          })
        );
      }
    } catch (error) {
      setMessageError(t('custom_field_operation_error_message'));
      setShowError(true);
    }
  };

  const IconFile = ({ file }) => {
    if (getIconDoc(file.type)) {
      return <WordIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconPdf(file.type)) {
      return <PdfIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconImage(file.type)) {
      return <ImgIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconCode(file.type)) {
      return <HtmlIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconSound(file.type)) {
      return <SoundIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconVideo(file.type)) {
      return <VideoIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconAccessDocument(file.type)) {
      return (
        <AccessDocumentIcon
          fill={colors.suvaGrey}
          className="control-panel__icon-type"
          width="20"
        />
      );
    }
    if (getIconPpt(file.type)) {
      return (
        <PowerPointIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />
      );
    }
    if (getIconZip(file.type)) {
      return <ZipIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    if (getIconExcel(file.type)) {
      return <ExcelIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
    }
    return <DocumentIcon fill={colors.suvaGrey} className="control-panel__icon-type" width="20" />;
  };

  IconFile.propTypes = {
    file: PropTypes.shape({
      type: PropTypes.string,
    }).isRequired,
  };

  const onDownload = async (currentDocument) => {
    try {
      const { name } = currentDocument;
      const { data, errors } = !showTypeNotifications
        ? await getDownload({
            url: `${window.API_V1}/cli/notifications/${cusNavId}/${
              currentClient?.clientId || clientId
            }/${encodeURIComponent(mail)}/${currentNotificationReviewId}/download/${
              currentDocument.id
            }`,
          })
        : await getMassiveDownload({
            url: `${window.API_V1}/gen/not/${cusNavId}/${encodeURIComponent(
              mail
            )}/${currentNotificationReviewId}/download/${currentDocument.id}`,
          });
      if (data) {
        const url = !showTypeNotifications
          ? `${window.REACT_APP_SERVER_URL}${window.API_V1}/cli/notifications/${cusNavId}/${
              currentClient?.clientId || clientId
            }/${encodeURIComponent(mail)}/${currentNotificationReviewId}/download/${
              currentDocument.id
            }`
          : `${window.REACT_APP_SERVER_URL}${
              window.API_V1
            }/gen/not/${cusNavId}/${encodeURIComponent(
              mail
            )}/${currentNotificationReviewId}/download/${currentDocument.id}`;
        const result = await fetch(url, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        const blob = await result.blob();
        const tempLink = window.document.createElement('a');
        tempLink.href = window.URL.createObjectURL(blob);
        tempLink.setAttribute('download', name);
        tempLink.click();
      }
      if (errors?.length) {
        throw new Error(
          errors.reduce((actual, current) => `${actual}\n${current.message}`),
          ''
        );
      }
    } catch (error) {
      setMessageError(t('custom_field_operation_error_message'));
      setShowError(true);
    }
  };

  const onClosePreviewModal = () => {
    setOpenPreviewModal(false);
  };

  const onCloseFormModal = () => {
    setOpenFormModal(false);
  };

  const openImageViewer = useCallback((data) => {
    setCurrentData(data);
    setIsViewerOpen(true);
  }, []);

  const viewDocument = (document, i) => (
    <React.Fragment key={document.id}>
      {document.type.toLowerCase() === '.png' || document.type.toLowerCase() === '.jpg' ? (
        <>
          {document.data && !document.isLoadingImg ? (
            <div
              className="upload-img-container mb-2 mr-2"
              aria-hidden
              onClick={() =>
                openImageViewer(`data:image/${document.type.split('.')[1]};base64,${document.data}`)
              }
            >
              <UploadedImage
                imageSrc={`data:image/${document.type.split('.')[1]};base64,${document.data}`}
                className="chat-upload-img"
              />
            </div>
          ) : (
            <Spinner />
          )}
        </>
      ) : (
        <AttachmentBox
          name={document.name}
          icon={<IconFile file={document} />}
          className="mr-2 mb-2 chat-attachment-box-component"
          index={i}
          isRemove={false}
          onDownload={() => onDownload(document)}
        />
      )}
    </React.Fragment>
  );

  return (
    <ChatWrapper mainColor={mainColor} maxAnswerLength={answers?.length}>
      <div className="chat-box-container">
        {answers?.length > 0 &&
          answers.map((answer, index) => (
            <React.Fragment key={answer.id}>
              {answer.createdBy !== mail && (
                <div className="d-flex mb-4">
                  <div>
                    <div className="chat-circle" id={`name-${index}`}>
                      <UncontrolledTooltip placement="top" target={`name-${index}`}>
                        {answer.createdByName
                          ? answer.createdByName?.substring(0, 1)
                          : answer.createdBy?.substring(0, 1)}
                      </UncontrolledTooltip>
                      <span className="chat-circle-text text-uppercase">
                        {answer.createdByName
                          ? answer.createdByName?.substring(0, 1)
                          : answer.createdBy?.substring(0, 1)}
                      </span>
                    </div>
                  </div>

                  <div className="ml-3 w-auto">
                    <span>{moment(answer.created).format('DD/MM/YYYY HH:mm')}</span>
                    <div className="chat-comment-box chat-color-box-secondary">
                      <article dangerouslySetInnerHTML={{ __html: answer.text }} />
                      {answer?.docs?.length > 0 && (
                        <div className="attachment-box-container mt-1">
                          <div className="attachment-box">
                            {answer?.docs.map((document, i) => viewDocument(document, i))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {answer.createdBy === mail && (
                <div className="d-flex mb-4 flex-row-reverse chat-info-main">
                  <div>
                    <div className="chat-circle chat-circle-color-main" id={`name-${index}`}>
                      <UncontrolledTooltip placement="top" target={`name-${index}`}>
                        {answer.createdByName
                          ? answer.createdByName?.substring(0, 1)
                          : answer.createdBy?.substring(0, 1)}
                      </UncontrolledTooltip>
                      <span className="chat-circle-text text-uppercase">
                        {answer.createdByName
                          ? answer.createdByName?.substring(0, 1)
                          : answer.createdBy?.substring(0, 1)}
                      </span>
                    </div>
                  </div>

                  <div className="mr-2 w-auto d-flex flex-column align-items-end">
                    <div className="chat-date-main">
                      <span>{moment(answer.created).format('DD/MM/YYYY HH:mm')}</span>
                    </div>
                    <div className="chat-comment-box chat-color-box-main">
                      <article dangerouslySetInnerHTML={{ __html: answer.text }} />
                      {answer?.docs?.length > 0 && (
                        <div className="attachment-box-container mt-1">
                          <div className="attachment-box">
                            {answer?.docs.map((document, i) => viewDocument(document, i))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        {!answers?.length && (
          <div className="d-flex flex-column justify-content-center align-items-center h-100">
            <ConversationIcon width={57} fill={colors.suvaGrey} height={65} className="mb-2" />
            <span>{t('notification_not_found_comment')}</span>
          </div>
        )}
      </div>
      <div>
        <NotificationCommentCard
          showTextEditor
          setAnswerText={setAnswerText}
          answerText={answerText}
          refRichTextEditor={refRichTextEditor}
        />
      </div>
      <div className="d-flex align-items-end">
        {!showTypeNotifications && (
          <div className="chat-upload-document-container">
            <ButtonUpload
              className="home__btn"
              type="button"
              icon={<UploaderIcon />}
              label={t('general_text_attachs')}
              color="primary"
              onClick={() => setOpenFormModal(true)}
            />
          </div>
        )}
        {isCustomer && (
          <ButtonPreview
            className="home__btn"
            type="button"
            icon={<EyeIcon />}
            label={t('general_text_view')}
            color="primary"
            onClick={() => setOpenPreviewModal(true)}
          />
        )}
      </div>

      <div className="mt-5 mb-4 d-flex justify-content-end">
        <Button
          className="home__btn text-uppercase mr-4"
          type="button"
          label={t('general_button_cancel')}
          color="secondary"
          onClick={() => {
            dispatch(setCurrentNotification(null));
            if (state?.isClientNotify) {
              dispatch(goBackClientNotify(true));
            }
            history.goBack();
          }}
        />
        <Button
          className="home__btn text-uppercase mr-4"
          type="button"
          label={t('general_button_send')}
          color="primary"
          onClick={isCustomer ? answerSave : answerContactSave}
          disabled={!(attachments.length !== 0 || (answerText !== '' && answerText !== null))}
        />
      </div>
      {(createAnswerLoading ||
        createMassiveAnswerLoading ||
        createGeneralContactAnswerLoading ||
        loadingDownload ||
        loadingMassiveDownload) && <MainSpinner />}
      <AlertModal
        type="error"
        isOpen={showError}
        toggle={onToggleError}
        message={messageError}
        okText={t('general_button_ok')}
        onOk={onToggleError}
      />
      <AttachFormModal isOpen={openPreviewModal} toggle={onClosePreviewModal} size="lg">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onClosePreviewModal}
        />
        <ModalBody>
          <PreviewNotification
            theme={theme}
            variant={PREVIEW_NOTIFICATION.CHAT}
            newName={newName}
            logoHeader={theme.logoHeader}
            mainColor={theme.mainColor}
            logoFooter={theme.whiteLogo}
            footerText={theme.footerText}
            phone={theme.phone}
            logoNotify={theme.logoNotify}
          />
        </ModalBody>
      </AttachFormModal>
      <AttachFormModal isOpen={openFormModal} toggle={onCloseFormModal} size="xl">
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={onCloseFormModal}
        />
        <ModalBody>
          <AttachForm onClose={onCloseFormModal} clientId={currentClient?.clientId || clientId} />
        </ModalBody>
      </AttachFormModal>
      <AttachFormModal
        isOpen={isViewerOpen}
        toggle={() => {
          setIsViewerOpen(false);
        }}
        size="xl"
      >
        <ModalHeader
          className="modal-header"
          backgroundColor={colors.transparent}
          onClose={() => {
            setIsViewerOpen(false);
          }}
        />
        <ModalBody>
          <div className="img-viewer-container">
            <UploadedImage imageSrc={currentData} className="img-viewer" />
          </div>
        </ModalBody>
      </AttachFormModal>
    </ChatWrapper>
  );
};

Chat.defaultProps = {
  newName: '',
};

Chat.propTypes = {
  newName: PropTypes.string,
};

export default Chat;
